import React, { useId, useRef, useState } from 'react';
import { Video, Upload, CheckCircle, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm';
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

/**
 * Video URL field with optional manual upload to Cloudinary via POST /api/upload/video.
 */
const VideoUrlInput = ({
  value,
  onChange,
  label = 'Video URL',
  focusRingClass = 'focus:ring-[#1a3884]',
}) => {
  const fileRef = useRef(null);
  const uploadId = useId();
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file (MP4, MOV, WEBM, etc.)');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error('Video must be under 100MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('video', file);

    try {
      const { data } = await api.post('/upload/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success && data.data?.url) {
        onChange(data.data.url);
        toast.success('Video uploaded successfully');
      } else {
        toast.error('Upload failed — no URL returned');
      }
    } catch (err) {
      console.error('Video upload error:', err);
      toast.error(err.response?.data?.message || 'Video upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const isCloudinary = Boolean(value?.includes('cloudinary.com'));
  const inputFocus = `focus:ring-2 ${focusRingClass} focus:border-transparent`;

  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>
      <div className="relative">
        <Video className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://youtube.com/watch?v=... or direct MP4 URL"
          className={`w-full pl-9 pr-4 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:bg-white dark:focus:bg-[#002147] transition-all ${inputFocus}`}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {['youtube', 'vimeo', 'external'].map((t) => (
          <button
            key={t}
            type="button"
            className="text-xs px-2.5 py-1 bg-white dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 text-slate-600 dark:text-slate-300 hover:border-[#1a3884] dark:hover:border-[#4f7dff] hover:text-[#1a3884] dark:hover:text-[#4f7dff] rounded-lg font-medium transition-colors capitalize"
          >
            {t}
          </button>
        ))}
        <input
          ref={fileRef}
          id={uploadId}
          type="file"
          accept={VIDEO_ACCEPT}
          className="hidden"
          disabled={uploading}
          onChange={handleFileSelect}
        />
        <label
          htmlFor={uploadId}
          className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold border transition-colors cursor-pointer ${
            uploading
              ? 'bg-slate-100 dark:bg-[#00152E] border-slate-200 dark:border-[#1a3884]/50 text-slate-500 dark:text-slate-500 cursor-not-allowed'
              : 'bg-white dark:bg-[#00152E] border-slate-200 dark:border-[#1a3884]/50 text-[#1a3884] dark:text-[#4f7dff] hover:border-[#1a3884] dark:hover:border-[#4f7dff] hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/20'
          }`}
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          {uploading ? 'Uploading…' : 'Manual Upload'}
        </label>
        {value && isCloudinary && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
            <CheckCircle className="w-3.5 h-3.5" />
            On Cloudinary
          </span>
        )}
        {value && (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#1a3884] dark:text-[#4f7dff] hover:underline font-medium"
          >
            Preview
          </a>
        )}
      </div>
    </div>
  );
};

export default VideoUrlInput;
